import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { DateInput } from "@/components/ui/date-input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { fmtHours } from "@/lib/utils"
import { IconDownload, IconX } from "@tabler/icons-react"
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

interface ReportSummary {
  total_hours: number
  billable_hours: number
  non_billable_hours: number
  utilization_rate: number
  entry_count: number
}

interface ByProject {
  project_id: number
  project_name: string
  customer_name: string
  total_hours: number
  billable_hours: number
  entry_count: number
}

interface ByEmployee {
  user_id: number
  user_name: string
  total_hours: number
  billable_hours: number
  entry_count: number
}

interface ByCustomer {
  customer_id: number
  customer_name: string
  total_hours: number
  billable_hours: number
  entry_count: number
}

interface DailyBreakdown {
  date: string
  total_hours: number
  billable_hours: number
}

export interface Report {
  summary: ReportSummary
  by_project: ByProject[]
  by_employee: ByEmployee[]
  by_customer: ByCustomer[]
  daily_breakdown: DailyBreakdown[]
}

export interface ReportFilters {
  from?: string
  to?: string
  user_id?: number
  project_id?: number
  customer_id?: number
}

interface Props {
  report: Report
  filters: ReportFilters
  users: { id: number; name: string; project_ids: number[] }[]
  projects: { id: number; name: string; customer_id: number; customer_name: string }[]
  customers: { id: number; name: string }[]
  onApplyFilters: (overrides: Record<string, string | undefined>) => void
  onClearFilters: () => void
  pdfUrl: string
}

const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#06b6d4", "#84cc16"]

function shortDate(dateStr: string) {
  const date = new Date(`${dateStr}T00:00:00`)
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

export default function ReportsView({
  report,
  filters,
  users,
  projects,
  customers,
  onApplyFilters,
  onClearFilters,
  pdfUrl,
}: Props) {
  function applyFilters(overrides: Record<string, string | undefined>) {
    const merged: Record<string, string | undefined> = {
      from: filters.from,
      to: filters.to,
      user_id: filters.user_id ? String(filters.user_id) : undefined,
      project_id: filters.project_id ? String(filters.project_id) : undefined,
      customer_id: filters.customer_id ? String(filters.customer_id) : undefined,
      ...overrides,
    }

    if (overrides.project_id) {
      const project = projects.find((p) => String(p.id) === overrides.project_id)
      if (project) {
        merged.customer_id = String(project.customer_id)
      }
      if (merged.user_id) {
        const user = users.find((u) => String(u.id) === merged.user_id)
        if (user && !user.project_ids.includes(Number(overrides.project_id))) {
          merged.user_id = undefined
        }
      }
    }

    if (overrides.customer_id !== undefined) {
      const currentProjectId = merged.project_id
      if (currentProjectId) {
        const project = projects.find((p) => String(p.id) === currentProjectId)
        if (project && String(project.customer_id) !== overrides.customer_id) {
          merged.project_id = undefined
        }
      }
      if (merged.user_id && overrides.customer_id) {
        const customerProjectIds = projects
          .filter((p) => String(p.customer_id) === overrides.customer_id)
          .map((p) => p.id)
        const user = users.find((u) => String(u.id) === merged.user_id)
        if (user && !user.project_ids.some((pid) => customerProjectIds.includes(pid))) {
          merged.user_id = undefined
        }
      }
    }

    if (overrides.user_id) {
      const user = users.find((u) => String(u.id) === overrides.user_id)
      if (user) {
        if (merged.project_id && !user.project_ids.includes(Number(merged.project_id))) {
          merged.project_id = undefined
        }
        if (merged.customer_id) {
          const customerProjectIds = projects
            .filter((p) => String(p.customer_id) === merged.customer_id)
            .map((p) => p.id)
          if (!user.project_ids.some((pid) => customerProjectIds.includes(pid))) {
            merged.customer_id = undefined
            merged.project_id = undefined
          }
        }
      }
    }

    onApplyFilters(merged)
  }

  function handleExportPdf() {
    window.location.href = pdfUrl
  }

  const filteredProjects = filters.customer_id
    ? projects.filter((p) => p.customer_id === filters.customer_id)
    : projects

  const relevantProjectIds = filters.project_id
    ? [filters.project_id]
    : filters.customer_id
      ? projects.filter((p) => p.customer_id === filters.customer_id).map((p) => p.id)
      : null

  const filteredUsers = relevantProjectIds
    ? users.filter((u) => u.project_ids.some((pid) => relevantProjectIds.includes(pid)))
    : users

  const filteredCustomers = filters.user_id
    ? (() => {
        const user = users.find((u) => u.id === filters.user_id)
        if (!user) return customers
        const customerIds = new Set(projects.filter((p) => user.project_ids.includes(p.id)).map((p) => p.customer_id))
        return customers.filter((c) => customerIds.has(c.id))
      })()
    : customers

  const visibleProjects = filters.user_id
    ? (() => {
        const user = users.find((u) => u.id === filters.user_id)
        if (!user) return filteredProjects
        return filteredProjects.filter((p) => user.project_ids.includes(p.id))
      })()
    : filteredProjects

  const hasFilters = filters.from || filters.to || filters.user_id || filters.project_id || filters.customer_id

  const dailyData = report.daily_breakdown.map((d) => ({
    name: shortDate(d.date),
    total: d.total_hours,
    billable: d.billable_hours,
  }))

  const projectPieData = report.by_project.map((p) => ({
    name: p.project_name,
    value: p.total_hours,
  }))

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-end">
        <Button size="sm" onClick={handleExportPdf}>
          <IconDownload className="mr-1 h-4 w-4" />
          Export PDF
        </Button>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label className="text-xs">From</Label>
          <DateInput value={filters.from} onChange={(iso) => applyFilters({ from: iso })} className="w-[130px]" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">To</Label>
          <DateInput value={filters.to} onChange={(iso) => applyFilters({ to: iso })} className="w-[130px]" />
        </div>
        <Select
          value={filters.user_id ? String(filters.user_id) : ""}
          onValueChange={(v) => applyFilters({ user_id: v || undefined })}
        >
          <SelectTrigger size="sm">
            <SelectValue placeholder="All Employees" />
          </SelectTrigger>
          <SelectContent>
            {filteredUsers.map((u) => (
              <SelectItem key={u.id} value={String(u.id)}>
                {u.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={filters.project_id ? String(filters.project_id) : ""}
          onValueChange={(v) => applyFilters({ project_id: v || undefined })}
        >
          <SelectTrigger size="sm">
            <SelectValue placeholder="All Projects" />
          </SelectTrigger>
          <SelectContent>
            {visibleProjects.map((p) => (
              <SelectItem key={p.id} value={String(p.id)}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={filters.customer_id ? String(filters.customer_id) : ""}
          onValueChange={(v) => applyFilters({ customer_id: v || undefined })}
        >
          <SelectTrigger size="sm">
            <SelectValue placeholder="All Customers" />
          </SelectTrigger>
          <SelectContent>
            {filteredCustomers.map((c) => (
              <SelectItem key={c.id} value={String(c.id)}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={onClearFilters}>
            <IconX className="mr-1 h-4 w-4" />
            Clear
          </Button>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="pt-0">
            <p className="text-muted-foreground text-xs">Total Hours</p>
            <p className="text-2xl font-bold">{fmtHours(report.summary.total_hours)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-0">
            <p className="text-muted-foreground text-xs">Billable Hours</p>
            <p className="text-2xl font-bold">{fmtHours(report.summary.billable_hours)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-0">
            <p className="text-muted-foreground text-xs">Non-Billable Hours</p>
            <p className="text-2xl font-bold">{fmtHours(report.summary.non_billable_hours)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-0">
            <p className="text-muted-foreground text-xs">Utilization Rate</p>
            <p className="text-2xl font-bold">{report.summary.utilization_rate}%</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Daily Trend</CardTitle>
          </CardHeader>
          <CardContent>
            {dailyData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={dailyData}>
                  <XAxis dataKey="name" fontSize={11} />
                  <YAxis fontSize={11} />
                  <Tooltip />
                  <Area
                    type="monotone"
                    dataKey="total"
                    stroke="#3b82f6"
                    fill="#3b82f6"
                    fillOpacity={0.1}
                    name="Total"
                  />
                  <Area
                    type="monotone"
                    dataKey="billable"
                    stroke="#10b981"
                    fill="#10b981"
                    fillOpacity={0.1}
                    name="Billable"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-muted-foreground flex h-[220px] items-center justify-center text-sm">
                No data for this period
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Hours by Project</CardTitle>
          </CardHeader>
          <CardContent>
            {projectPieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={projectPieData}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${fmtHours(value)}`}
                  >
                    {projectPieData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-muted-foreground flex h-[220px] items-center justify-center text-sm">
                No project data
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {report.by_employee.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Hours by Employee</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={Math.max(200, report.by_employee.length * 40)}>
              <BarChart data={report.by_employee} layout="vertical">
                <XAxis type="number" fontSize={11} />
                <YAxis type="category" dataKey="user_name" fontSize={11} width={100} />
                <Tooltip />
                <Bar dataKey="billable_hours" fill="#10b981" name="Billable" stackId="hours" />
                <Bar dataKey="total_hours" fill="#3b82f6" name="Total" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {report.by_project.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Breakdown by Project</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Project</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead className="text-right">Total Hours</TableHead>
                  <TableHead className="text-right">Billable Hours</TableHead>
                  <TableHead className="text-right">Entries</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {report.by_project.map((row) => (
                  <TableRow key={row.project_id}>
                    <TableCell className="font-medium">{row.project_name}</TableCell>
                    <TableCell>{row.customer_name}</TableCell>
                    <TableCell className="text-right font-mono">{fmtHours(row.total_hours)}</TableCell>
                    <TableCell className="text-right font-mono">{fmtHours(row.billable_hours)}</TableCell>
                    <TableCell className="text-right">{row.entry_count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {report.by_customer.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Breakdown by Customer</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead className="text-right">Total Hours</TableHead>
                  <TableHead className="text-right">Billable Hours</TableHead>
                  <TableHead className="text-right">Entries</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {report.by_customer.map((row) => (
                  <TableRow key={row.customer_id}>
                    <TableCell className="font-medium">{row.customer_name}</TableCell>
                    <TableCell className="text-right font-mono">{fmtHours(row.total_hours)}</TableCell>
                    <TableCell className="text-right font-mono">{fmtHours(row.billable_hours)}</TableCell>
                    <TableCell className="text-right">{row.entry_count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
